import React, { useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Tabs,
  Table,
  Tag,
  Drawer,
  Form,
  Input,
  Select,
  message,
  List,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  MessageOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

const STATUS_COLORS = {
  Open: 'blue',
  Pending: 'orange',
  Resolved: 'green',
  Closed: 'default',
};

const PRIORITY_COLORS = {
  Low: 'default',
  Medium: 'gold',
  High: 'red',
};

const WhiteLabelSupport = () => {
  const [activeTab, setActiveTab] = useState('my');
  const [newDrawerOpen, setNewDrawerOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [saving, setSaving] = useState(false);
  const [replying, setReplying] = useState(false);
  const [form] = Form.useForm();
  const [replyForm] = Form.useForm();
  const [myStatusFilter, setMyStatusFilter] = useState('All');
  const [clientStatusFilter, setClientStatusFilter] = useState('All');

  // Mock data
  const myTickets = useMemo(() => ([
    { id: 'WL-1001', subject: 'White-label branding issue', status: 'Open', priority: 'High', createdAt: '2025-08-10 14:32', lastUpdate: '2025-08-11 09:10', to: 'Pathmarkr Support' },
    { id: 'WL-1002', subject: 'Billing discrepancy for August', status: 'Pending', priority: 'Medium', createdAt: '2025-08-12 10:05', lastUpdate: '2025-08-12 15:21', to: 'Pathmarkr Support' },
    { id: 'WL-1003', subject: 'Feature request: custom domains per client', status: 'Resolved', priority: 'Low', createdAt: '2025-08-14 08:01', lastUpdate: '2025-08-16 17:45', to: 'Pathmarkr Product' },
  ]), []);

  const clientTickets = useMemo(() => ([
    { id: 'CT-4301', client: 'Acme Corp', subject: 'Cannot access analytics', status: 'Open', priority: 'High', createdAt: '2025-08-18 12:40', lastUpdate: '2025-08-18 12:45' },
    { id: 'CT-4302', client: 'Northwind', subject: 'Invoice not received', status: 'Pending', priority: 'Medium', createdAt: '2025-08-19 09:33', lastUpdate: '2025-08-19 10:10' },
    { id: 'CT-4303', client: 'Globex', subject: 'User role confusion', status: 'Open', priority: 'Low', createdAt: '2025-08-20 11:20', lastUpdate: '2025-08-20 11:55' },
  ]), []);

  const filteredMyTickets = useMemo(() => {
    if (myStatusFilter === 'All') return myTickets;
    return myTickets.filter(t => t.status === myStatusFilter);
  }, [myTickets, myStatusFilter]);

  const filteredClientTickets = useMemo(() => {
    if (clientStatusFilter === 'All') return clientTickets;
    return clientTickets.filter(t => t.status === clientStatusFilter);
  }, [clientTickets, clientStatusFilter]);

  const ticketMessages = useMemo(() => ({
    'CT-4301': [
      { author: 'Acme Corp', content: 'Our analytics page shows a blank chart.', datetime: '2025-08-18 12:40' },
      { author: 'You', content: 'Thanks, could you try clearing cache?', datetime: '2025-08-18 12:45' },
    ],
    'CT-4302': [
      { author: 'Northwind', content: 'We did not receive the July invoice.', datetime: '2025-08-19 09:33' },
    ],
    'CT-4303': [
      { author: 'Globex', content: 'What permissions does "Analyst" include?', datetime: '2025-08-20 11:20' },
    ],
  }), []);

  const baseColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 110 },
    { title: 'Subject', dataIndex: 'subject', key: 'subject' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120, render: (s) => (
      <Tag color={STATUS_COLORS[s] || 'default'}>{s}</Tag>
    ) },
    { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 120, render: (p) => (
      <Tag color={PRIORITY_COLORS[p] || 'default'}>{p}</Tag>
    ) },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 180 },
    { title: 'Updated', dataIndex: 'lastUpdate', key: 'lastUpdate', width: 180 },
  ];

  const myColumns = [
    ...baseColumns,
    { title: 'To', dataIndex: 'to', key: 'to', width: 180 },
    { title: 'Actions', key: 'actions', width: 160, render: (_, r) => (
      <Space>
        <Button icon={<MessageOutlined />} onClick={() => openDetails(r)}>View</Button>
      </Space>
    ) },
  ];

  const clientColumns = [
    { title: 'Client', dataIndex: 'client', key: 'client', width: 180 },
    ...baseColumns,
    { title: 'Actions', key: 'actions', width: 220, render: (_, r) => (
      <Space>
        <Button icon={<MessageOutlined />} onClick={() => openDetails(r)}>Reply</Button>
      </Space>
    ) },
  ];

  const openNewTicket = () => {
    form.resetFields();
    setNewDrawerOpen(true);
  };

  const openDetails = (ticket) => {
    setSelectedTicket(ticket);
    replyForm.resetFields();
    setDetailsOpen(true);
  };

  const submitNewTicket = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      // TODO: POST to backend support endpoint
      void values;
      message.success('Ticket submitted');
      setNewDrawerOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error('Failed to submit ticket');
    } finally {
      setSaving(false);
    }
  };

  const submitReply = async () => {
    try {
      const values = await replyForm.validateFields();
      setReplying(true);
      // TODO: POST reply to backend
      void values;
      message.success('Reply sent');
      setDetailsOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error('Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Support</Title>
          <Text type="secondary">Raise tickets to Pathmarkr and manage tickets from your clients.</Text>
        </div>

        <Card bodyStyle={{ padding: 12 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={12}>
              <Space>
                <CustomerServiceOutlined />
                <Text type="secondary">Support center for your white-label agency</Text>
              </Space>
            </Col>
            <Col xs={24} md={12} style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={openNewTicket}>New Ticket</Button>
            </Col>
          </Row>
        </Card>

        <Card bodyStyle={{ padding: 0 }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: 'my', label: 'My Tickets (to Pathmarkr)', children: (
                <Space direction="vertical" style={{ width: '100%', padding: 12 }} size={8}>
                  <Space>
                    <Text type="secondary">Status:</Text>
                    <Select
                      size="small"
                      value={myStatusFilter}
                      style={{ width: 160 }}
                      onChange={setMyStatusFilter}
                      options={[
                        { value: 'All', label: 'All' },
                        { value: 'Open', label: 'Open' },
                        { value: 'Pending', label: 'Pending' },
                        { value: 'Resolved', label: 'Resolved' },
                        { value: 'Closed', label: 'Closed' },
                      ]}
                    />
                  </Space>
                  <Table rowKey="id" columns={myColumns} dataSource={filteredMyTickets} pagination={{ pageSize: 8 }} />
                </Space>
              ) },
              { key: 'clients', label: 'Client Tickets', children: (
                <Space direction="vertical" style={{ width: '100%', padding: 12 }} size={8}>
                  <Space>
                    <Text type="secondary">Status:</Text>
                    <Select
                      size="small"
                      value={clientStatusFilter}
                      style={{ width: 160 }}
                      onChange={setClientStatusFilter}
                      options={[
                        { value: 'All', label: 'All' },
                        { value: 'Open', label: 'Open' },
                        { value: 'Pending', label: 'Pending' },
                        { value: 'Resolved', label: 'Resolved' },
                        { value: 'Closed', label: 'Closed' },
                      ]}
                    />
                  </Space>
                  <Table rowKey="id" columns={clientColumns} dataSource={filteredClientTickets} pagination={{ pageSize: 8 }} />
                </Space>
              ) },
            ]}
          />
        </Card>

        {/* New Ticket Drawer */}
        <Drawer
          title="Create Support Ticket"
          open={newDrawerOpen}
          onClose={() => setNewDrawerOpen(false)}
          width={560}
          destroyOnClose
        >
          <Form layout="vertical" form={form}>
            <Form.Item label="Subject" name="subject" rules={[{ required: true, message: 'Enter a subject' }]}> 
              <Input placeholder="Describe your issue" />
            </Form.Item>
            <Form.Item label="Priority" name="priority" rules={[{ required: true, message: 'Select priority' }]}>
              <Select
                placeholder="Select priority"
                options={[
                  { value: 'Low', label: 'Low' },
                  { value: 'Medium', label: 'Medium' },
                  { value: 'High', label: 'High' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Category" name="category">
              <Select
                placeholder="Select category"
                options={[
                  { value: 'Branding', label: 'Branding' },
                  { value: 'Billing', label: 'Billing' },
                  { value: 'Integrations', label: 'Integrations' },
                  { value: 'Analytics', label: 'Analytics' },
                  { value: 'Other', label: 'Other' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Details" name="details" rules={[{ required: true, message: 'Provide details' }]}> 
              <Input.TextArea rows={5} placeholder="Steps to reproduce, screenshots, etc." />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setNewDrawerOpen(false)}>Cancel</Button>
                <Button type="primary" loading={saving} onClick={submitNewTicket}>Submit</Button>
              </Space>
            </div>
          </Form>
        </Drawer>

        {/* Ticket Details & Reply */}
        <Drawer
          title={selectedTicket ? `${selectedTicket.id} • ${selectedTicket.subject}` : 'Ticket'}
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          width={720}
          destroyOnClose
        >
          {selectedTicket && (
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {selectedTicket.client && (
                <Text type="secondary">Client: {selectedTicket.client}</Text>
              )}
              <Space>
                <Tag color={STATUS_COLORS[selectedTicket.status] || 'default'}>{selectedTicket.status}</Tag>
                <Tag color={PRIORITY_COLORS[selectedTicket.priority] || 'default'}>{selectedTicket.priority}</Tag>
              </Space>
              <Divider style={{ margin: '8px 0' }} />
              <List
                header={<Text strong>Conversation</Text>}
                dataSource={ticketMessages[selectedTicket.id] || []}
                itemLayout="horizontal"
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={(
                        <Space size={8} wrap>
                          <Text strong>{item.author}</Text>
                          <Text type="secondary">{item.datetime}</Text>
                        </Space>
                      )}
                      description={<Text>{item.content}</Text>}
                    />
                  </List.Item>
                )}
              />
              <Card size="small" title="Reply">
                <Form layout="vertical" form={replyForm}>
                  <Form.Item name="message" rules={[{ required: true, message: 'Enter a reply' }]}> 
                    <Input.TextArea rows={4} placeholder="Type your reply to the ticket" />
                  </Form.Item>
                  <div style={{ textAlign: 'right' }}>
                    <Space>
                      <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                      <Button type="primary" loading={replying} onClick={submitReply}>Send Reply</Button>
                    </Space>
                  </div>
                </Form>
              </Card>
            </Space>
          )}
        </Drawer>
      </Space>
    </div>
  );
};

export default WhiteLabelSupport;
